import {
  Avatar,
  Box,
  Button,
  Center,
  Flex,
  IconButton,
  Tooltip,
  Img,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ScaleFade,
  Text,
  useColorModeValue,
  useDisclosure
} from "@chakra-ui/react";
import React, { useContext, useEffect, useState } from "react";
import FullPageLoader from "../../components/FullPageLoader";
import Container from "../../components/Container";
import Header from "../../components/Header";
import { transition } from "../..";
import Footer from "../../components/footer";
import { COLORS } from "../../constants/colors";
import { BsPlus, BsFillPlusCircleFill, BsPencilSquare  } from "react-icons/bs";
import { addBtnStyles } from "../issue report";
import { getFamily } from "../idCard/services";
import { UserProfileContext } from "../../context/userProfileContext";
import { AuthContext } from "../../context/authContext";
import {
  errorNotifier,
  successNotifier
} from "../../components/NotificationHandler";
import { AddFamilyMemberForm } from "./components/AddFamilyMemberForm";
import { EditFamilyMemberForm } from "./components/EditFamilyMemberForm";
import { useNavigate } from "react-router-dom";
import { PROTECTED_PATHS } from "../../app/constants";

export const FamilyMembers = () => {
  const appTextColor = useColorModeValue(COLORS.black, COLORS.white);
  const { userProfile } = useContext(UserProfileContext);
  const { userInfo } = useContext(AuthContext);
  const [family, setFamily] = useState([]);
  const [loading, setLoading] = useState(false);
  const cardBg = useColorModeValue(COLORS.lightGrey, COLORS.white);
  const bgColor = useColorModeValue(COLORS.primary, COLORS.green);
  const { userId } = userInfo;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { 
    isOpen: isEditOpen, 
    onOpen: onEditOpen, 
    onClose: onEditClose 
  } = useDisclosure();
  const [selectedMember, setSelectedMember] = useState(null);
  const navigate = useNavigate();

  const handleEditMember = (member) => {
    setSelectedMember(member);
    onEditOpen();
  };

  // Format relationship to capitalize first letter
  const formatRelationship = (relationship) => {
    if (!relationship) return "Family";
    return relationship.charAt(0).toUpperCase() + relationship.slice(1);
  };

  const sortedFamily = [...family].sort((a, b) => {
    if (a.createdAt && b.createdAt) {
      return new Date(b.createdAt) - new Date(a.createdAt);
    }
    return 0;
  });

  // Fetch family members
  useEffect(() => {
    const fetchData = async () => {
      try {
        await getFamily(userId, setLoading, setFamily);
      } catch (error) {
        errorNotifier("An error occurred while fetching family members.");
      }
    };
    fetchData();
  }, [userId]);

  return loading ? (
    <FullPageLoader />
  ) : (
    <ScaleFade initialScale={0.3} in={true} {...transition}>
      <Box>
        <Box shadow="sm">
          <Container>
            <Header menu />
          </Container>
        </Box>
        <Flex pt="20px">
          <Container>
            <Center w="100%" py={4}>
              <Button
                variant="outline"
                bg="transparent"
                border={`2px solid ${bgColor}`}
                color={bgColor}
                rounded="full"
                size="lg"
                onClick={onOpen}
                _hover={{
                  bg: bgColor,
                  color: "white"
                }}
              >
                <BsPlus size="2.5rem" pr={2} />
                Add Family Member
              </Button>
            </Center>

            {/* Family Members List */}
            {family.length > 0 ? (
              <>
                <Box maxHeight={"400px"} pr={"5"} pb={"2.5rem"} overflowY={"auto"} sx={scrollbarStyle}>
                  {sortedFamily.map((member, i) => (
                    <Box
                      key={i}
                      bg={COLORS.primary}
                      p="15px"
                      rounded={"lg"}
                      mb="10px"
                    >
                      <Flex
                        justify={"space-between"}
                        w={"100%"}
                        align={"center"}
                      >
                        <Avatar
                          src={member?.passport}
                          name={member?.familyName}
                          alt="profile"
                        />
                        <Flex direction="column" flex="1" ml={3}>
                          <Text color={cardBg} fontWeight="bold">
                            {member?.familyName || "N/A"} (
                            {formatRelationship(member?.relationship)})
                          </Text>
                          {member.familyPhone && (
                            <Text
                              color={cardBg}
                              fontSize="sm"
                              fontWeight={"black"}
                            >
                              {member.familyPhone}
                            </Text>
                          )}
                        </Flex>
                        <Flex gap={2} align="center">
                          <Text
                            bg={cardBg}
                            color={COLORS.primary}
                            p={"5px 10px"}
                            rounded={"full"}
                          >
                            {member?.status}
                          </Text>
                         <Tooltip label="Edit Family Member" placement="top" hasArrow>
  <IconButton
    icon={<BsPencilSquare />}
    aria-label="Edit family member"
    onClick={() => handleEditMember(member)}
    variant="ghost"
    colorScheme="white"
    color="white"
    size="sm"
    transform="rotate(180deg)"
  />
</Tooltip>
                        </Flex>
                      </Flex>
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Center flexDirection="column" h="70vh" w="100%">
                <Img src="/empty.svg" alt="empty" />
                <Text
                  py="5"
                  fontWeight="500"
                  textAlign="center"
                  color={appTextColor}
                >
                  You do not have any family members yet
                </Text>
              </Center>
            )}

            {/* Add Family Member Button */}
            <Flex {...addBtnStyles}>
              <BsFillPlusCircleFill
                color={bgColor}
                size={"2.5rem"}
                onClick={onOpen}
              />
            </Flex>
          </Container>
        </Flex>

        {/* Add Family Member Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="md">
          <ModalOverlay />
          <ModalContent py={5} borderRadius="10px">
            <ModalHeader>Add Family Member</ModalHeader>
            <ModalCloseButton />
            <ModalBody py={{ base: 4, md: 4, lg: 6 }}>
              <AddFamilyMemberForm
                onClose={onClose}
                userId={userId}
                refreshFamily={() => getFamily(userId, setLoading, setFamily)}
              />
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Edit Family Member Modal */}
        <Modal isOpen={isEditOpen} onClose={onEditClose} size="md">
          <ModalOverlay />
          <ModalContent py={5} borderRadius="10px">
            <ModalHeader>Edit Family Member</ModalHeader>
            <ModalCloseButton />
            <ModalBody py={{ base: 4, md: 4, lg: 6 }}>
              <EditFamilyMemberForm
                onClose={onEditClose}
                userId={userId}
                member={selectedMember}
                refreshFamily={() => getFamily(userId, setLoading, setFamily)}
              />
            </ModalBody>
          </ModalContent>
        </Modal>

        <Footer />
      </Box>
    </ScaleFade>
  );
};
const scrollbarStyle = {
  "&::-webkit-scrollbar": {
    width: "4px"
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1"
  },
  "&::-webkit-scrollbar-thumb": {
    background: COLORS.primary,
    borderRadius: "24px"
  }
};
export default FamilyMembers;
